import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Phone,
  Globe,
  MapPin,
  Building2,
  Languages,
  ArrowLeft,
  MessageSquare,
  Navigation,
  Share2,
  ShieldCheck,
  Clock3,
} from "lucide-react";

import { fetchDoctor } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { ConfidenceBar } from "../components/ConfidenceBar";
import { DisclaimerBanner } from "../components/DisclaimerBanner";
import { ReportDialog } from "../components/ReportDialog";
import { DetailSkeleton } from "../components/Skeletons";
import { Button } from "../components/ui/button";

const formatDate = (iso) => {
  if (!iso || iso === "0000-00-00 00:00:00") return "Recently reviewed";

  try {
    return new Date(iso).toLocaleString("en-CA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso || "Recently reviewed";
  }
};

const getReviewDate = (doctor = {}) => {
  return (
    doctor.last_updated ||
    doctor.updated_at ||
    doctor.admin_verified_at ||
    doctor.admin_reviewed_at ||
    doctor.last_verified
  );
};

const normalizeWebsiteUrl = (website) => {
  if (!website) return "";

  const cleanWebsite = String(website).trim();

  if (
    cleanWebsite.startsWith("http://") ||
    cleanWebsite.startsWith("https://")
  ) {
    return cleanWebsite;
  }

  return `https://${cleanWebsite}`;
};

const getProviderType = (doctor = {}) => {
  const raw = `${doctor.provider_type || ""} ${doctor.specialty || ""} ${
    doctor.clinic_name || ""
  }`.toLowerCase();

  if (raw.includes("walk")) return "walkin";
  if (raw.includes("pediatric")) return "pediatrics";
  if (raw.includes("primary")) return "primary";

  return "family";
};

const providerBadge = (doctor) => {
  const type = getProviderType(doctor);

  if (type === "walkin") {
    return {
      label: "Walk-in Clinic",
      className:
        "bg-gradient-to-r from-[#14B87A]/15 to-[#38D39F]/15 text-[#0F8A5F] border-[#B7E4C7]",
    };
  }

  if (type === "pediatrics") {
    return {
      label: "Pediatrics",
      className:
        "bg-gradient-to-r from-[#356DFF]/10 to-[#5B8CFF]/10 text-[#356DFF] border-[#BFD3FF]",
    };
  }

  if (type === "primary") {
    return {
      label: "Primary Care",
      className:
        "bg-gradient-to-r from-[#14B87A]/15 to-[#38D39F]/15 text-[#0F8A5F] border-[#B7E4C7]",
    };
  }

  return {
    label: "Family Doctor",
    className:
      "bg-gradient-to-r from-[#356DFF]/10 to-[#5B8CFF]/10 text-[#356DFF] border-[#BFD3FF]",
  };
};

const directionsUrl = (doctor) => {
  const destination = [
    doctor.clinic_name,
    doctor.address,
    doctor.city,
    "Ontario",
    doctor.postal_code,
  ]
    .filter(Boolean)
    .join(", ");

  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    destination
  )}`;
};

export default function DoctorDetail() {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      setData(await fetchDoctor(id));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <DetailSkeleton />
      </div>
    );
  }

  if (!data) return null;

  const { doctor, reports } = data;
  const badge = providerBadge(doctor);
  const providerType = getProviderType(doctor);
  const isAdminVerified = Number(doctor.admin_verified) === 1;
  const reviewDate = getReviewDate(doctor);
  const websiteUrl = normalizeWebsiteUrl(doctor.website);

  const shareClinic = async () => {
    const shareData = {
      title: doctor.name,
      text: `${doctor.name} - ${doctor.clinic_name || "Ontario clinic"}`,
      url: window.location.href,
    };

    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      await navigator.clipboard.writeText(window.location.href);
      alert("Clinic link copied.");
    }
  };

  return (
    <div
      data-testid="doctor-detail-page"
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 overflow-x-hidden"
    >
      <Link
        to="/doctors"
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1E4256] font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to results
      </Link>

      <div className="mt-5 rounded-[2rem] bg-white border border-slate-100 p-5 sm:p-8 lg:p-10 shadow-2xl overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${badge.className}`}
              >
                {badge.label}
              </span>

              <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                {doctor.specialty}
              </span>

              {isAdminVerified && (
                <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold border border-emerald-200">
                  Admin Verified
                </span>
              )}
            </div>

            <h1
              data-testid="doctor-name"
              className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-slate-900 leading-tight break-words"
            >
              {doctor.name}
            </h1>

            <div className="mt-3 text-sm text-slate-500">
              Last updated {formatDate(doctor.last_updated)} · Call clinic to
              confirm availability.
            </div>
          </div>

          <div className="shrink-0">
            <StatusBadge status={doctor.status} size="lg" />
          </div>
        </div>

        <div className="mt-8 grid gap-4 text-sm text-slate-700">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
            <span className="break-words">{doctor.clinic_name}</span>
          </div>

          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
            <span className="break-words">
              {doctor.address}, {doctor.city}, ON {doctor.postal_code || ""}
            </span>
          </div>

          {doctor.phone && (
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
              <a
                className="text-[#356DFF] hover:underline break-all"
                href={`tel:${doctor.phone}`}
              >
                {doctor.phone}
              </a>
            </div>
          )}

          {doctor.website && (
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
              <a
                className="text-[#356DFF] hover:underline break-all"
                target="_blank"
                rel="noopener noreferrer"
                href={websiteUrl}
              >
                {doctor.website}
              </a>
            </div>
          )}

          {doctor.languages?.length ? (
            <div className="flex items-start gap-3">
              <Languages className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
              <span className="break-words">{doctor.languages.join(", ")}</span>
            </div>
          ) : null}
        </div>

        <div className="mt-8 max-w-lg overflow-hidden">
          <ConfidenceBar score={doctor.confidence_score} />
        </div>

        <div className="mt-8">
          <div className="rounded-[2rem] border border-slate-200 bg-gradient-to-br from-white to-[#F8FBFF] p-5 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
                  Provider Availability
                </p>

                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <StatusBadge status={doctor.status} size="lg" />

                  <span className="text-sm text-slate-500">
                    Last updated {formatDate(doctor.last_updated)}
                  </span>
                </div>

                <p className="mt-3 text-sm leading-7 text-slate-600 max-w-2xl">
                  Availability information is based on public clinic data,
                  verification checks, and community reports. Please call the
                  clinic directly before visiting.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 lg:min-w-[360px]">
                {doctor.phone && (
                  <a
                    href={`tel:${doctor.phone}`}
                    className="flex-1 flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#356DFF] to-[#5B8CFF] text-white px-5 py-4 text-sm font-semibold shadow-lg hover:opacity-95 transition"
                  >
                    <Phone className="w-5 h-5" />
                    Call Clinic
                  </a>
                )}

                <a
                  href={directionsUrl(doctor)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
                >
                  <Navigation className="w-5 h-5" />
                  Directions
                </a>

                <button
                  type="button"
                  onClick={shareClinic}
                  className="flex-1 flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
                >
                  <Share2 className="w-5 h-5" />
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>

        {isAdminVerified ? (
          <div className="mt-6 rounded-2xl border border-[#B7E4C7] bg-[#F0FFF4] p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#2D6A4F] mt-0.5 shrink-0" />

              <div className="w-full">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold border border-emerald-200">
                    Admin Verified
                  </span>
                </div>

                <p className="mt-3 font-semibold text-[#2D6A4F]">
                  Verified Information
                </p>

                <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                  This listing is continuously reviewed using public clinic
                  information, admin verification, and community-reported
                  updates where available. Always contact the clinic directly
                  before making healthcare decisions.
                </p>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-2xl bg-white/80 border border-[#D8EFE4] px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                      Verification Source
                    </p>

                    <p className="mt-1 font-semibold text-slate-800">
                      {doctor.verification_source || "Admin Review"}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white/80 border border-[#D8EFE4] px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                      Last Reviewed
                    </p>

                    <p className="mt-1 font-semibold text-slate-800">
                      {formatDate(reviewDate)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-purple-200 bg-purple-50 p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-purple-700 mt-0.5 shrink-0" />

              <div className="w-full">
                <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold border border-purple-200">
                  Not Admin Verified
                </span>

                <p className="mt-3 font-semibold text-purple-700">
                  Verification Pending
                </p>

                <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                  This provider has not yet been verified by the PinMyDoc admin
                  team. Please contact the clinic directly before visiting.
                </p>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-2xl bg-white/80 border border-purple-100 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                      Verification Source
                    </p>

                    <p className="mt-1 font-semibold text-slate-800">
                      Pending Review
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white/80 border border-purple-100 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                      Last Updated
                    </p>

                    <p className="mt-1 font-semibold text-slate-800">
                      {formatDate(doctor.last_updated)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {providerType === "walkin" && (
          <div className="mt-4 rounded-2xl border border-[#FED7AA] bg-[#FFF7ED] p-4">
            <div className="flex items-start gap-3">
              <Clock3 className="w-5 h-5 text-[#C2410C] mt-0.5 shrink-0" />

              <div>
                <p className="font-semibold text-[#9A3412]">
                  Walk-in Clinic Notice
                </p>

                <p className="mt-1 text-sm text-slate-700">
                  Walk-in clinic hours and wait times may change daily. Please
                  call the clinic before visiting.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8">
          <Button
            onClick={() => setOpen(true)}
            className="w-full sm:w-auto rounded-2xl bg-[#2D6A4F] hover:bg-[#24543E] text-white px-6 py-3 text-sm font-semibold"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Report Availability Update
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <DisclaimerBanner variant="inline" />
      </div>

      <div className="mt-10">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
          Recent community reports
        </h2>

        <div className="mt-5 space-y-4">
          {reports?.length === 0 && (
            <div className="rounded-[2rem] border border-slate-100 bg-white p-8 text-sm text-slate-600 shadow-lg">
              No reports yet. Be the first to share what you learned.
            </div>
          )}

          {reports?.map((r) => (
            <div
              key={r.id}
              className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-lg overflow-hidden"
            >
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={r.report_type} />

                <span className="text-xs text-slate-500">
                  {formatDate(r.created_at)}
                </span>
              </div>

              {r.note && (
                <p className="mt-3 text-sm text-slate-700 leading-relaxed break-words">
                  {r.note}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      <ReportDialog
        open={open}
        onOpenChange={setOpen}
        doctor={doctor}
        onSubmitted={load}
      />
    </div>
  );
}